import React, {useMemo} from "react";
import {Typography} from "antd";
import {NotificationOutlined} from "@ant-design/icons";
import {useTranslation} from "react-i18next";
import {formatMomentDate} from "../../../../utils/helpers";
import {StepDescriptionTypes} from "./StepTypes";
import moment from "moment";

const {Text} = Typography;

const formatTime = dateString => {
    if (!dateString) return "";
    return moment(dateString).format("HH:mm");
};

const StepDescription = ({
    state,
    canceled,
    passed,
    startDate,
    reorderDate,
    endDate,
    mergedAt,
    deliveryCreatedAt,
    assignedCourier,
    renderProofIcon,
    orderInformations,
    receiverName
}: StepDescriptionTypes) => {
    const {t} = useTranslation(["orderDetail"]);

    const startTime = useMemo(() => {
        if (state === "waiting") {
            return formatMomentDate(deliveryCreatedAt);
        } else if (startDate) {
            return formatMomentDate(startDate);
        } else {
            return "";
        }
    }, [state, deliveryCreatedAt, startDate]);

    const endTime = useMemo(() => {
        if (state === "onDeliveryAddress" && passed) {
            t("passed", {ns: "orderDetail"});
        } else if (endDate) {
            return formatMomentDate(endDate);
        } else {
            return "";
        }
    }, [state, passed, endDate]);

    const renderMergedAt = (
        <div key={Math.random()}>
            <Text type="warning">{t("mergedAt", {ns: "orderDetail"})}: </Text>
            {formatMomentDate(mergedAt)}
        </div>
    );

    const renderStartTime = useMemo(() => {
        return (
            <div key={Math.random()}>
                <Text type="success">{t("fields.start", {ns: "common"})} : </Text>
                {startTime}
            </div>
        );
    }, [startTime, deliveryCreatedAt, startDate, state]);

    const renderReorderDate = (
        <div key="reorderDate">
            <Text type="warning">{t("reorderedByCourier", {ns: "orderDetail"})}: </Text>
            {formatMomentDate(reorderDate)}
        </div>
    );

    const renderEndTime = useMemo(() => {
        if (!endDate) {
            return null;
        }

        let startMoment;
        if (state === "waiting") {
            startMoment = moment(deliveryCreatedAt);
        } else if (startDate) {
            startMoment = moment(startDate);
        } else {
            return null;
        }

        const endMoment = moment(endDate);

        // Süre farkını hesapla (milisaniye cinsinden)
        const duration = moment.duration(endMoment.diff(startMoment));

        // Dakika ve saniye hesapla
        const totalSeconds = Math.floor(duration.asSeconds());
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;

        let timeString = "";
        if (minutes > 0) {
            timeString += `${minutes}dk`;
        }
        if (seconds > 0 || minutes === 0) {
            if (timeString) timeString += " ";
            timeString += `${seconds}sn`;
        }

        return (
            <div key={Math.random()}>
                <Text type="danger"> {t("fields.elapsedDuration", {ns: "common"})}: </Text>
                {timeString}
            </div>
        );
    }, [endDate, startDate, deliveryCreatedAt, state]);

    const renderNotifications = useMemo(() => {
        if (!orderInformations || orderInformations.length === 0) {
            return null;
        }

        // Mevcut step state'ine uygun bildirimleri filtrele
        const stateNotifications = orderInformations.filter(
            notification => notification.meta?.state === state && notification.isSent
        );

        if (stateNotifications.length === 0) {
            return null;
        }

        return stateNotifications.map((notification, index) => {
            const time = formatTime(notification.sentAt);
            const notificationReceiverName = notification.orderReceiverName || receiverName;

            return (
                <div key={index} style={{marginTop: "4px"}}>
                    <NotificationOutlined style={{marginRight: "4px", color: "#1890ff"}} />
                    <Text type="secondary" style={{fontSize: "12px"}}>
                        {time} {notificationReceiverName}
                    </Text>
                </div>
            );
        });
    }, [orderInformations, state, receiverName, endDate]);

    const renderApproachNotifications = useMemo(() => {
        if (!orderInformations || orderInformations.length === 0) {
            return null;
        }

        // Sadece belirli state'lerde approach bildirimlerini göster
        let approachState = null;
        if (state === "onTheWayForPickUp") {
            approachState = "approachToCompany";
        } else if (state === "onDelivery") {
            approachState = "approachToReceiver";
        }

        if (!approachState) {
            return null;
        }

        // Approach bildirimlerini filtrele
        const approachNotifications = orderInformations.filter(
            notification => notification.meta?.state === approachState && notification.isSent
        );

        if (approachNotifications.length === 0) {
            return null;
        }
        return approachNotifications.map((notification, index) => {
            const time = formatTime(notification.sentAt);
            const notificationReceiverName = notification.orderReceiverName || receiverName;
            const distance = notification.meta?.approachDistanceInMeters;

            return (
                <div key={`approach-${index}`} style={{marginTop: "4px"}}>
                    <NotificationOutlined style={{marginRight: "4px", color: "#1890ff"}} />
                    <Text type="secondary" style={{fontSize: "12px"}}>
                        {time} {notificationReceiverName} - kurye yaklaştı ({distance}m)
                    </Text>
                </div>
            );
        });
    }, [orderInformations, state, receiverName]);

    const renderContent = () => {
        const renders = [];
        if (state === "onDeliveryAddress" && mergedAt) renders.push(renderMergedAt);
        if (reorderDate) renders.push(renderReorderDate);
        if (startDate) renders.push(renderStartTime);
        if (endDate) {
            if (renderEndTime) renders.push(renderEndTime);
            if (state === "onDeliveryAddress") {
                renders.push(
                    <div key={Math.random()}>
                        <Text type="warning">
                            {canceled
                                ? t("stepInfo.canceled", {ns: "orderDetail"})
                                : passed
                                ? t("stepInfo.passed", {ns: "orderDetail"})
                                : t("stepInfo.productsDelivered", {
                                      ns: "orderDetail",
                                      courierName: assignedCourier
                                  })}
                        </Text>
                        {renderProofIcon()}
                    </div>
                );
            }
        } else if (state === "onPickUpAddress") {
            renders.push(
                <div key={Math.random()}>
                    <Text type="warning">
                        {canceled
                            ? t("stepInfo.canceled", {ns: "orderDetail"})
                            : passed
                            ? t("stepInfo.passed", {ns: "orderDetail"})
                            : endDate &&
                              t("stepInfo.productsReveived", {
                                  ns: "orderDetail",
                                  courierName: assignedCourier
                              })}
                    </Text>
                    {renderProofIcon()}
                </div>
            );
        }

        if (renderNotifications) {
            renders.push(renderNotifications);
        }
        if (renderApproachNotifications) {
            renders.push(renderApproachNotifications);
        }

        return <>{renders}</>;
    };

    return <>{renderContent()}</>;
};

export default StepDescription;
